/**
 * Official vendor help pages for unblocking camera/microphone access, per
 * browser family and app locale. Linked from the permission hint card on the
 * pre-join screen (RoomNameScreen): the hint copy is the same in every
 * browser, and this link carries the browser-specific steps.
 *
 * Verification (2026-11-03): every Google, Apple and Microsoft URL was fetched
 * directly and served the camera/microphone article in the requested language
 * (the English Apple URLs redirect to a regional English path). Mozilla serves
 * a CAPTCHA to non-interactive clients and answers HTTP 200 even for slugs
 * that don't exist, so the six Mozilla URLs were verified against Wayback
 * Machine snapshots of the real articles instead (desktop: en Dec 2025, de May
 * 2025, fr Apr 2026; Android: all three Jun 2026). Known gaps: Microsoft has
 * no Edge-specific page (the Windows privacy page has an Edge section), Samsung
 * has no page for Samsung Internet's per-site settings (Android's
 * app-permission page is the best official fallback), and Mozilla's Firefox
 * for Android article explains app permissions rather than walking through
 * site settings.
 */

export type PermissionHelpLocale = 'en' | 'de' | 'fr';

export type PermissionHelpBrowser =
  | 'chrome-desktop'
  | 'chrome-android'
  | 'chrome-ios'
  | 'safari-macos'
  | 'safari-ios'
  | 'safari-ipados'
  | 'edge-desktop'
  | 'firefox-desktop'
  | 'firefox-android'
  | 'samsung-internet'
  | 'other-ios'
  | 'other-android'
  | 'other';

export interface PermissionHelpLinks {
  /** How to allow the site in the browser's own settings. */
  browserSettings?: string;
  /** The OS-level toggle for the browser app itself (iOS/Android), when the browser depends on it. */
  systemSettings?: string;
}

type LocalizedUrl = (locale: PermissionHelpLocale) => string;

const google =
  (product: 'chrome' | 'android', answer: string, platform?: 'Android' | 'iOS'): LocalizedUrl =>
  (locale) =>
    `https://support.google.com/${product}/answer/${answer}?hl=${locale}${
      platform ? `&co=GENIE.Platform%3D${platform}` : ''
    }`;

const APPLE_LOCALE_PATH: Record<PermissionHelpLocale, string> = { en: '', de: 'de-de/', fr: 'fr-fr/' };
const apple =
  (guide: 'safari' | 'iphone' | 'ipad', page: string, platform: 'mac' | 'ios' | 'ipados'): LocalizedUrl =>
  (locale) =>
    `https://support.apple.com/${APPLE_LOCALE_PATH[locale]}guide/${guide}/${page}/${platform}`;

const MICROSOFT_LOCALE_PATH: Record<PermissionHelpLocale, string> = { en: 'en-us', de: 'de-de', fr: 'fr-fr' };
const microsoft =
  (page: string): LocalizedUrl =>
  (locale) =>
    `https://support.microsoft.com/${MICROSOFT_LOCALE_PATH[locale]}/windows/privacy/${page}`;

const mozilla =
  (slugs: Record<PermissionHelpLocale, string>): LocalizedUrl =>
  (locale) =>
    `https://support.mozilla.org/${locale === 'en' ? 'en-US' : locale}/kb/${slugs[locale]}`;

const chromeDesktop = google('chrome', '2693767');
const chromeAndroid = google('chrome', '2693767', 'Android');
const chromeIos = google('chrome', '2693767', 'iOS');
const androidAppPermissions = google('android', '9431959');
const iosAppPermissions = apple('iphone', 'iph168c4bbd5', 'ios');
const safariMac = apple('safari', 'ibrwe2159f50', 'mac');
const safariIos = apple('iphone', 'iphb01fc3c85', 'ios');
const safariIpados = apple('ipad', 'ipad8ea0fc1a', 'ipados');
const windowsCameraPrivacy = microsoft('windows-camera-microphone-and-privacy');
const firefoxDesktop = mozilla({
  en: 'how-manage-your-camera-and-microphone-permissions',
  de: 'kamera-und-mikrofonberechtigungen-verwalten',
  fr: 'gerer-permissions-camera-et-microphone',
});
const firefoxAndroid = mozilla({
  en: 'how-firefox-android-use-permissions-it-requests',
  de: 'wie-nutzt-firefox-fuer-android-die-berechtigungen',
  fr: 'comment-firefox-android-utilise-autorisations',
});

const LINKS: Record<PermissionHelpBrowser, { browserSettings?: LocalizedUrl; systemSettings?: LocalizedUrl }> = {
  'chrome-desktop': { browserSettings: chromeDesktop },
  'chrome-android': { browserSettings: chromeAndroid, systemSettings: androidAppPermissions },
  'chrome-ios': { browserSettings: chromeIos, systemSettings: iosAppPermissions },
  'safari-macos': { browserSettings: safariMac },
  'safari-ios': { browserSettings: safariIos },
  'safari-ipados': { browserSettings: safariIpados },
  'edge-desktop': { browserSettings: windowsCameraPrivacy },
  'firefox-desktop': { browserSettings: firefoxDesktop },
  'firefox-android': { browserSettings: firefoxAndroid, systemSettings: androidAppPermissions },
  // Samsung has no page for Samsung Internet's per-site permissions.
  'samsung-internet': { systemSettings: androidAppPermissions },
  'other-ios': { systemSettings: iosAppPermissions },
  'other-android': { systemSettings: androidAppPermissions },
  other: {},
};

const SUPPORTED_LOCALES: PermissionHelpLocale[] = ['en', 'de', 'fr'];

function toHelpLocale(locale: string): PermissionHelpLocale {
  const language = locale.toLowerCase().split(/[-_]/)[0] as PermissionHelpLocale;
  return SUPPORTED_LOCALES.includes(language) ? language : 'en';
}

/** Resolves the help links for a browser family in the app locale (e.g. 'de-DE'), falling back to English. */
export function getPermissionHelpLinks(browser: PermissionHelpBrowser, locale: string): PermissionHelpLinks {
  const helpLocale = toHelpLocale(locale);
  const entry = LINKS[browser];
  return {
    ...(entry.browserSettings ? { browserSettings: entry.browserSettings(helpLocale) } : {}),
    ...(entry.systemSettings ? { systemSettings: entry.systemSettings(helpLocale) } : {}),
  };
}

/**
 * First-cut user-agent classification into the families above. Good enough
 * to pick a help page; not a general browser detector. `maxTouchPoints`
 * distinguishes iPadOS, which reports itself as a Mac.
 */
export function detectPermissionHelpBrowser(
  userAgent = navigator.userAgent,
  maxTouchPoints = navigator.maxTouchPoints
): PermissionHelpBrowser {
  const isIpad = /iPad/.test(userAgent) || (/Macintosh/.test(userAgent) && maxTouchPoints > 1);
  const isIos = isIpad || /iPhone|iPod/.test(userAgent);
  const isAndroid = /Android/.test(userAgent);

  if (isIos) {
    if (/CriOS/.test(userAgent)) return 'chrome-ios';
    if (/FxiOS|EdgiOS|OPT\/|DuckDuckGo|GSA\//.test(userAgent)) return 'other-ios';
    if (/Safari\//.test(userAgent)) return isIpad ? 'safari-ipados' : 'safari-ios';
    return 'other-ios';
  }
  if (isAndroid) {
    if (/SamsungBrowser/.test(userAgent)) return 'samsung-internet';
    if (/Firefox\//.test(userAgent)) return 'firefox-android';
    if (/Chrome\//.test(userAgent) && !/EdgA|OPR\//.test(userAgent)) return 'chrome-android';
    return 'other-android';
  }
  if (/Edg\//.test(userAgent)) return 'edge-desktop';
  if (/Firefox\//.test(userAgent)) return 'firefox-desktop';
  if (/Chrome\//.test(userAgent) && !/OPR\//.test(userAgent)) return 'chrome-desktop';
  if (/Safari\//.test(userAgent)) return 'safari-macos';
  return 'other';
}
